'use strict';

/*
  PlaidService

  Handles communication with FE Plaid API and SOA Plaid related endpoints.

  Parameters:
    presenter [APIPresenter] (required):
      Reference to the APIPresenter in order to communicate with SOA and create data models.

    configObj [object][optional]:
      properties:
        errorMap: [hasDefault]
          Key/Value pairs of Plaid error codes and Mogo BE error groups.
          These values (errors) are mapped to trigger force close event
          for the Plaid iframe.

      IE:
        configObj = {
          errorMap: {
              INSTITUTION_NOT_AVAILABLE: 'ACTION_TRY_AGAIN_LATER',
              INSTITUTION_NO_LONGER_SUPPORTED: 'NOT_SUPPORTED',
              etc..
          }
        }
*/

function PlaidService(presenter, configObj) {
  this._presenter = presenter;
  this._analytics = new Analytics();
  this._history = new History();

  this._defaultErrorMap = {
    INSTITUTION_NOT_AVAILABLE: 'ACTION_TRY_AGAIN_LATER',
    INSTITUTION_NO_LONGER_SUPPORTED: 'NOT_SUPPORTED',
    ITEM_NOT_SUPPORTED: 'NOT_SUPPORTED',
    MFA_NOT_SUPPORTED: 'NOT_SUPPORTED',
    INVALID_LINK_TOKEN: 'UNEXPECTED_ERROR',
    LINK_TOKEN_EXPIRED: 'UNEXPECTED_ERROR',
    RATE_LIMIT: 'UNEXPECTED_ERROR',
    NO_ACCOUNTS: 'NO_ACCOUNTS',
    ITEM_NO_ERROR: 'ITEM_NO_ERROR'
  };

  this._DEBUG = false;
  if (this._DEBUG) this.pageName = '[PlaidService]';

  if (configObj) {
    this._configErrorMap = configObj.errorMap;
  }

  this._errorMap = this._configErrorMap ? this._configErrorMap : this._defaultErrorMap;
  this._onSuccessCallback = null;
  this._onFailureCallback = null;

  this._apiHandler = null; // Plaid JS Api handler

  this.pre_selected_institution = null;

  this._init = function () {
    if (this._DEBUG) console.log(this.pageName + ' init() called');

    this.plaidJSPromise = this._loadPlaidJSLib();
  };

  // create a link to a users bank account
  this.linkAccount = function (initData, successCallback, failureCallback) {
    if (this._DEBUG) console.log(this.pageName + ' linkAccount() called');

    this._presenter.createModel('banking.plaid', { data: initData });
    this._onSuccessCallback = successCallback;
    this._onFailureCallback = failureCallback;
    $.when(this.plaidJSPromise).then(this._createPlaidApiHandler.bind(this, initData));
  };

  this.generateLinkAccountPayload = function (args) {
    var payload = {
      public_token: args[0],
      metadata: args[1]
    };
    return payload;
  };

  this.generateUpdateAccountPayload = function (data) {
    var payload = {};
    if (typeof data[0] === 'string') {
      payload.public_token = data[0];
    }
    return payload;
  };

  this._createPlaidApiHandler = function (data) {
    if (this._DEBUG) console.log(this.pageName + ' Creating Plaid API Handler with data:', data);

    if (!data.link_token) {
      console.error('PlaidService: missing critical Plaid API data - link_token.');
      if (this._onFailureCallback) {
        this._onFailureCallback({ errors: [{ code: 500, key: 'missing_link_token' }] });
      }
      return;
    }

    var options = {
      token: data.link_token,
      onLoad: this._onApiLoad.bind(this),
      onSuccess: this._onApiSuccess.bind(this),
      onExit: this._onApiExit.bind(this),
      onEvent: this._onApiEvent.bind(this)
    };

    if (this._DEBUG) console.log(this.pageName + ' Initializing Plaid with options:', options);

    this._apiHandler = Plaid.create(options);

    // Quick auto select...
    // TODO: prob should be passed in so presenter not needed.
    this.pre_selected_institution = this._presenter.getModelProperty('bankSelected.plaid_institution_id');
    if (this.pre_selected_institution) {
      if (this._DEBUG) console.log(this.pageName + ' Opening with pre-selected institution:', this.pre_selected_institution);
      this._apiHandler.open(this.pre_selected_institution);
    } else {
      if (this._DEBUG) console.log(this.pageName + ' Opening without pre-selected institution');
      this._apiHandler.open();
    }
  };

  this._onApiLoad = function () {
    if (this._DEBUG) console.log(this.pageName + ' Plaid iframe loaded');
    // this._analytics.forcePushEvent('todo');
  };

  this._onApiEvent = function (eventName, metadata) {
    if (this._DEBUG) console.log(this.pageName + ' _onApiEvent() called - ' + eventName, metadata);

    // Send Event analytics
    this._sendApiEventAnalytics(eventName, metadata);

    // Exit Plaid for errors we want to support ourselves with better messaging
    if (eventName === 'ERROR') {
      if (this._DEBUG) {
        console.log(this.pageName + ' _onApiEvent() - Registered event as Error: ' + JSON.stringify(metadata));
      }

      // Send error info to backend
      this._sendErrorEvent(metadata);
      // Track error
      this._pushErrorAnalytics(this._mappedForceExitErrorCode(metadata.error_code) || metadata.error_code);

      var liquidABSStep = '/member/dashboard/loans/application/connect-to-bank-service';
      var currentFlow = mogo.registry.mogoUI.repo.Portal.presenter.models.appState.layout.current_uri;
      var isMiniFlow = liquidABSStep !== currentFlow;

      if (isMiniFlow) {
        // Only hide iframe and go to dlt-fallback if error is not these two
        if (metadata.error_code === 'INVALID_CREDENTIALS' || metadata.error_code === 'INVALID_MFA') {
          if (this._DEBUG) {
            console.log('Error Code is \'INVALID_CREDENTIALS || INVALID_MFA, allow retry via Plaid: ' + JSON.stringify(metadata));
          }
        } else {
          /*
            Close Plaid iframe, show them DLT error page because Plaid ABS has failed           
           */
          $("[id*='plaid-link-iframe']").remove(); // this._apiHandler.exit({ force: true }) not working - https://plaid.com/docs/link/web/#exit
          this._history.goToUrl('/member/dashboard/loans/apply/bank-statement/dlt-fallback-error');
        }
      } else {
        // This is the other flow (Liquid)
        // Exit plaid iframe
        if (this._mappedForceExitErrorCode(metadata.error_code) !== null) {
          this._apiHandler.exit({ force: true });
        } else {
          if (this._DEBUG) {
            console.log(this.pageName + ' _onApiEvent() - Not exiting Plaid iFrame because error code is not included in _mappedForceExitErrorCode function');
          }
        }
      }
    }
  };

  this._onApiSuccess = function (public_token, metadata) {
    if (this._DEBUG) console.log(this.pageName + ' _onApiSuccess() called with token:', public_token);

    // Call the success callback with the public token and metadata
    if (this._onSuccessCallback) {
      try {
        this._onSuccessCallback([public_token, metadata]);
      } catch (error) {
        console.error('Error in success callback:', error);
        // If success callback fails, treat it as a failure
        if (this._onFailureCallback) {
          this._onFailureCallback({ errors: [{ code: 500, key: 'callback_error' }] });
        }
      }
    }
  };

  this._onApiExit = function (public_token, err, metadata) {
    if (this._DEBUG) console.log(this.pageName + ' _onApiExit() called');

    if (this._onFailureCallback) {
      this._onFailureCallback(arguments);
    }
    this._presenter.removeModelProperty('banking.plaid');
    // metadata contains information about the institution
    // that the user selected and the most recent API request IDs.
    // Storing this information can be helpful for support.
    // this._analytics.forcePushEvent('todo');
  };

  // Helper function
  this._mappedForceExitErrorCode = function (errorCode) {
    var mappedErrorCode = null;

    if (this._errorMap.hasOwnProperty(errorCode)) {
      mappedErrorCode = this._errorMap[errorCode];
    }
    return mappedErrorCode;
  };

  this._loadPlaidJSLib = function () {
    if (typeof Plaid === 'undefined') {
      return $.ajax({
        url: 'https://cdn.plaid.com/link/v2/stable/link-initialize.js',
        dataType: 'script',
        cache: true
      });
    }
  };

  this._sendApiEventAnalytics = function (eventName, metadata) {
    // List of meta available as of plaid version 2019-05029
    // https: //plaid.com/docs/#onevent-callback
    // Possible metaData
    // metadata = {
    // .error_code
    // .error_message,
    // .error_type,
    // .exit_status,
    // .institution_id,
    // .institution_name,
    // .institution_search_query,
    // .link_session_id,
    // .mfa_type,
    // .request_id,
    // .timestamp,
    // .view_name
    // }

    this._analytics.forcePushEvent('plaid-action', {
      action: [eventName],
      psid: [metadata.link_session_id],
      view: [metadata.view_name],
      query: [metadata.institution_search_query],
      institution: [metadata.institution_name],
      MFA: [metadata.mfa_type],
      Code: [metadata.error_code],
      Msg: [metadata.error_message],
      Key: [metadata.error_type],
      Exit: [metadata.exit_status]
    });
  };

  this._pushErrorAnalytics = function (errorType) {
    this._analytics.forcePushEvent('virtual-page-view', {
      Page: [window.location.href + '/error']
    });

    this._analytics.pushEvent('error-notification', {
      errorNotificationProduct: ['Plaid Error'],
      errorNotificationContent: [errorType]
    });
  };

  /*
    Sends Error data to backend
      errorData argument is the metadata provided by the Plaid API 
      _onApiEvent. See: https://plaid.com/docs/link/web/#onevent
  */

  this._sendErrorEvent = function (errorData) {
    if (this._DEBUG) console.log(this.pageName + ' _sendErrorEvent() called');

    var keys = ['error_type', 'error_code', 'error_message', 'exit_status', 'request_id', 'institution_id', 'link_session_id'];

    for (var key in errorData) {
      if (keys.indexOf(key) === -1 || errorData[key] == null) {
        delete errorData[key];
      }
    }
    errorData.client = 'web';
    if (this._presenter.hasAction('banking.plaid.create_plaid_error_occurrence')) {
      this._presenter.performAction('banking.plaid.create_plaid_error_occurrence', errorData);
    }
  };

  this._init();
}